import { useState, useEffect, useRef, useMemo } from "react"
import { DateTime } from "luxon"
import { useSelector, useDispatch } from "react-redux"
import ConfigContainer from "components/ConfigContainer"
import { Container, Title, Select, Text, Number, Checkbox } from "components/Form"
import { Button } from "react-bootstrap"
import { Popup } from "components"
import { FiTrash2, FiSettings, FiPlus } from "react-icons/fi"
import { FaArrowUp } from "react-icons/fa"
import { sampleView, limsProductTestPlan, limsSampleQc, limsBatchHold } from "state_management"
import useT from "utils/hooks/useT"
import { displaySampleId, restoreSampleIdSuffix } from "utils/scripts/sampleId"

// LIMS F-C1: the QC sample-login fields are all OPTIONAL and live entirely in local state,
// gated on showLims. This is the empty shape used for both add (new sample) and as the reset
// baseline; the actual values are persisted separately via the limsSampleQc slice (the
// deployed /samples endpoint rejects unknown fields → 422).
const EMPTY_QC = {
  batchLot: "",
  sampleType: "",
  quantity: null,
  quantityUnit: "",
  sampledBy: null,
  receivedBy: null,
  storageCondition: "",
  uniqueSampleId: "",
}

// A form component reused for both Add and Edit sample operations.
export default function AddEditSamplePopup({
  initial = {},
  analysisCollection = [],
  productCollection: _productCollection = [],
  onCancel = () => {},
  onSubmit = () => {},
  title = "Edit sample",
  isEdit = false,
}) {
  const { t } = useT("common")
  const teamTimezone = useSelector((state) => state.teamInfo?.timezone || 'UTC')
  const dispatch = useDispatch()

  const [form, setForm] = useState({
    sampleName: initial.sampleName || "",
    // For edit mode analyses should start empty (user can only add new ones)
    analysisName: initial.analysisName || [],
    observationId: initial.observationId || "1",
    numberOfObservations: initial.numberOfObservations || 1,
    arrival: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('yyyy-MM-dd') : DateTime.now().setZone(teamTimezone).startOf('day').toFormat('yyyy-MM-dd'),
    arrivalTime: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('HH:mm') : '00:00',
    deadline: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('yyyy-MM-dd') : "",
    deadlineTime: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('HH:mm') : '23:59',
    priority: initial.priority || 2,
    status: initial.status || 'TODO',
    productName: initial.productName || null,
    exclusive: !!initial.exclusive,
    isFlagged: !!initial.isFlagged,
  })


  // Access product analysis relationships from store to auto-fill analyses when selecting a product
  const productAnalysis = useSelector((state) => state.sampleView?.productAnalysis || {})

  // LIMS F-B2 "auto-derive test plan": when LIMS is on, the curated product test plan
  // (analysis-definition ids, keyed by product name) supersedes the auto-tracked
  // productAnalysis history as the source for pre-filling the Analyses field. Gated on
  // showLims so the non-LIMS Add Sample behaviour is byte-for-byte unchanged.
  const showLims = useSelector((state) => state.teamStatus?.showLims)
  const productTestPlans = useSelector((state) => state.limsProductTestPlan)

  // LIMS F-C1 "QC sample login": optional QC metadata captured in a collapsible "QC details"
  // section, shown only when LIMS is on. Kept entirely in local state and persisted SEPARATELY
  // via the limsSampleQc slice (the /samples endpoint rejects unknown fields → 422). Members
  // power the sampled-by / received-by selects (we store member ids). Everything here is gated
  // on showLims so the non-LIMS Add/Edit Sample behaviour is byte-for-byte unchanged.
  const members = useSelector((state) => state.members)
  const sampleQcStore = useSelector((state) => state.limsSampleQc)
  // The sample id for edit mode (passed through `initial` by the row element). New samples have
  // no id yet — it is obtained from the create response (see submit()).
  const qcSampleId = isEdit ? (initial.sampleId || initial.id || null) : null
  // SPEC-048 R14 (M8): the backend refuses (406) changing a sample's batch/lot while its CURRENT
  // (saved) lot is under an active hold — relabelling would sneak the sample out of a lot-keyed
  // quarantine. Ghost the batch-lot field for that case (the saved lot, not the in-progress edit).
  const savedLotHeld = useSelector((state) => {
    if (!showLims || !isEdit || !qcSampleId) return false
    const stored = limsSampleQc.selectSampleQc(state, qcSampleId)
    const lot = stored && stored.batchLot
    return !!lot && limsBatchHold.selectIsBatchRejected(state, lot)
  })
  const [showQc, setShowQc] = useState(false)
  const [qc, setQc] = useState(EMPTY_QC)
  const memberCollection = useMemo(
    () =>
      Object.values(members || {})
        .map((m) => ({ id: m.id, name: m.initials || m.email || m.id }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [members]
  )

  // State for showing analyses popup
  const [showAnalysesPopup, setShowAnalysesPopup] = useState(false)

  // Filter analysisCollection to exclude analyses already on the sample when editing
  const availableAnalysisCollection = useMemo(() => {
    if (!isEdit || !initial.analysisName || initial.analysisName.length === 0) {
      return analysisCollection
    }
    
    // Get the initial analysis IDs to exclude
    const initialAnalysisIds = Array.isArray(initial.analysisName) 
      ? initial.analysisName 
      : [initial.analysisName]
    
    // Filter out analyses that are already on the sample
    return analysisCollection.filter(analysis => 
      !initialAnalysisIds.includes(analysis.id)
    )
  }, [isEdit, initial.analysisName, analysisCollection])

  // Build product collection from productAnalysis instead of sampleGroups
  const productCollectionFromAnalysis = useMemo(() => {
    const productNames = Object.keys(productAnalysis).filter((name) => name && name !== '')
    return productNames.map((name) => ({ id: name, name: name }))
  }, [productAnalysis])

  // Keep track of previous product selection so we only auto-fill on change
  const prevProductRef = useRef(form.productName)

  // Track if the current product is from the predefined list (not creatable) or user-created (creatable)
  const isProductFromList = useRef(false)

  // When `initial` or `isEdit` changes (popup opened for different item), reset form state
  useEffect(() => {
    setForm({
      sampleName: initial.sampleName || "",
      analysisName: isEdit ? [] : (initial.analysisName || []),
      observationId: initial.observationId || "1",
      numberOfObservations: initial.numberOfObservations || 1,
      arrival: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('yyyy-MM-dd') : DateTime.now().setZone(teamTimezone).startOf('day').toFormat('yyyy-MM-dd'),
      arrivalTime: initial.arrival ? DateTime.fromISO(initial.arrival).setZone(teamTimezone).toFormat('HH:mm') : '00:00',
      deadline: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('yyyy-MM-dd') : "",
      deadlineTime: initial.deadline ? DateTime.fromISO(initial.deadline).setZone(teamTimezone).toFormat('HH:mm') : '23:59',
      priority: initial.priority || 2,
      status: initial.status || 'TODO',
      productName: initial.productName || null,
      exclusive: !!initial.exclusive,
      isFlagged: !!initial.isFlagged,
    })
    prevProductRef.current = initial.productName || null
    // Check if initial product is from the predefined list
    isProductFromList.current = initial.productName ? productCollectionFromAnalysis.some(p => p.id === initial.productName) : false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, isEdit, teamTimezone])

  // LIMS only: load the curated product test plans once on mount so a product selection can
  // pre-fill the Analyses from its plan. No-op (and no network) when LIMS is off.
  useEffect(() => {
    if (showLims) {
      dispatch(limsProductTestPlan.fetch())
    }
  }, [showLims, dispatch])

  // LIMS F-C1: load the QC details collection once on mount so an edited sample's stored QC can
  // be prefilled. No-op (and no network) when LIMS is off.
  useEffect(() => {
    if (showLims) {
      dispatch(limsSampleQc.fetch())
    }
  }, [showLims, dispatch])

  // LIMS F-C1: when editing an existing sample with LIMS on, prefill the QC section from its
  // stored details (keyed by sample id). Re-runs when the loaded collection arrives or the
  // edited sample changes. New samples (add mode) start from EMPTY_QC.
  useEffect(() => {
    if (!showLims) return
    if (isEdit && qcSampleId) {
      const stored = limsSampleQc.selectSampleQc({ limsSampleQc: sampleQcStore }, qcSampleId)
      setQc(stored ? { ...EMPTY_QC, ...stored } : EMPTY_QC)
    } else {
      setQc(EMPTY_QC)
    }
  }, [showLims, isEdit, qcSampleId, sampleQcStore])

  // LIMS F-B2 "auto-derive test plan": when LIMS is on, pre-fill the Analyses from the selected
  // product's CURATED test plan (analysis-definition ids — which map 1:1 onto the Analyses
  // field's value). Mirrors the legacy productAnalysis effect's change-detection via
  // prevProductRef (seeded with the initial product in the reset effect) so it only fires when
  // the user actively selects/changes the product — never on initial load (and never at all for
  // an existing sample, guarded by isEdit). Replaces the legacy effect entirely while LIMS is on.
  useEffect(() => {
    if (isEdit || !showLims) return
    const product = form.productName || null
    // No product / 'None' selected: nothing to derive (the None onChange already cleared it).
    if (product === "" || product === null) return
    const prev = prevProductRef.current || ""
    if (product === prev) return

    const plan = limsProductTestPlan.selectProductPlan(
      { limsProductTestPlan: productTestPlans },
      product
    )
    const ids = (plan && plan.analysisDefinitionIds) || []
    // Only pre-fill when a curated plan exists. If the product has NO curated plan, leave the
    // Analyses field unchanged (user picks manually) — do NOT clear it.
    if (ids.length > 0) {
      setForm((prevForm) => ({ ...prevForm, analysisName: ids }))
    }
    prevProductRef.current = product
  }, [form.productName, productTestPlans, isEdit, showLims])

  // Auto-select analyses when adding a sample and productName changes
  useEffect(() => {
    // When LIMS is on, the curated test plan owns Add-Sample pre-fill (the F-B2 effect above);
    // skip the legacy productAnalysis-history fill entirely so it can never clobber/clear it.
    if (isEdit || showLims) return
    const product = form.productName || null
    // If no product is selected (null) or the 'None' option is selected (empty string), do not auto-load analyses
    if (product === '' || product === null) return
    const prev = prevProductRef.current || ''
    if (product === prev) return

    // Get analyses for the selected product from the productAnalysis object
    const analysesForProduct = productAnalysis[product]
    if (!analysesForProduct || !Array.isArray(analysesForProduct) || analysesForProduct.length === 0) {
      // Clear analyses when product has no analyses defined
      setForm((prev) => ({ ...prev, analysisName: [] }))
      prevProductRef.current = product
      return
    }

    // Map analysis names to canonical ids from analysisCollection
    const normalized = analysesForProduct.map((item) => {
      const analysisName = item.analysis_name
      const foundByName = analysisCollection.find((ac) => String(ac.name) === String(analysisName))
      if (foundByName) return foundByName.id
      // fallback: maybe the name is actually an id or other identifier
      const foundById = analysisCollection.find((ac) => String(ac.id) === String(analysisName))
      if (foundById) return foundById.id
      return null
    }).filter(Boolean)

    if (normalized.length > 0) {
      setForm((prev) => ({ ...prev, analysisName: normalized }))
    }
    prevProductRef.current = product
  }, [form.productName, productAnalysis, analysisCollection, isEdit, showLims])

  const [showMore, setShowMore] = useState(false)

  // LIMS F-C1: derive the created sample id from the createSample response so the QC details can
  // be persisted against it. samples.createSample resolves to res.samples (array) | res.sample
  // (object) | res. We key by the PRIMARY (first) created sample row.
  // TODO(multi-observation): when numberOfObservations > 1 the backend creates one sample row
  // per observation; we only attach the QC details to the first created row here. A future SP-E
  // change should let QC details span all observation rows of a logical sample.
  const extractCreatedSampleId = (res) => {
    if (!res) return null
    let first
    if (Array.isArray(res.samples)) {
      first = res.samples[0]
    } else if (res.sample) {
      first = res.sample
    } else if (Array.isArray(res)) {
      first = res[0]
    } else {
      first = res
    }
    if (!first) return null
    return first.sampleId || first.id || first.sample_id || null
  }

  // True when the QC section has any user-entered value worth persisting. Avoids writing an
  // empty record (and an audit entry) for a sample whose QC details were never touched.
  const qcHasContent = (data) =>
    !!(
      data &&
      ((data.batchLot && data.batchLot.trim()) ||
        (data.sampleType && data.sampleType.trim()) ||
        data.quantity != null ||
        (data.quantityUnit && data.quantityUnit.trim()) ||
        data.sampledBy ||
        data.receivedBy ||
        (data.storageCondition && data.storageCondition.trim()) ||
        (data.uniqueSampleId && data.uniqueSampleId.trim()))
    )

  const submit = () => {
    // Build payload differently for edit vs add
    const analysisIds = (Array.isArray(form.analysisName) ? form.analysisName : [form.analysisName]).filter(Boolean)

    // Edit mode: only allow specific fields and ability to add analyses
    if (isEdit) {
      if (!form.sampleName || !form.deadline) return
      const deadlineISO = DateTime.fromISO(`${form.deadline}T${form.deadlineTime || '23:59'}`, { zone: teamTimezone }).toUTC().toISO()
      const payload = {
        sampleName: form.sampleName,
        priority: parseInt(form.priority, 10),
        deadline: deadlineISO,
        observationId: form.observationId,
        analysisIds: analysisIds,
      }
      // LIMS F-C1: persist the (optional) QC details for the edited sample, keyed by its id.
      // Fully gated on showLims; with LIMS off this is identical to the original behaviour. We
      // also require qcHasContent so saving a plain edit on a never-QC'd sample writes nothing
      // (no empty record / audit noise). Editing a sample that already HAS stored QC always
      // writes, so clearing fields is honoured.
      if (showLims && qcSampleId && (qcHasContent(qc) || limsSampleQc.selectSampleQc({ limsSampleQc: sampleQcStore }, qcSampleId))) {
        dispatch(limsSampleQc.setForSample(qcSampleId, qc)).catch(() => {})
      }
      onSubmit(payload)
      return
    }

    // Add mode: full create payload
    if (!form.sampleName || !analysisIds.length || !form.arrival || !form.deadline) return
    const arrivalISO = DateTime.fromISO(`${form.arrival}T${form.arrivalTime || '00:00'}`, { zone: teamTimezone }).toUTC().toISO()
    const deadlineISO = DateTime.fromISO(`${form.deadline}T${form.deadlineTime || '23:59'}`, { zone: teamTimezone }).toUTC().toISO()
    const payload = {
      sampleName: form.sampleName,
      analysisIds: analysisIds,
      observationId: form.observationId,
      numberOfObservations: parseInt(form.numberOfObservations, 10) || 1,
      arrival: arrivalISO,
      deadline: deadlineISO,
      priority: parseInt(form.priority, 10),
      status: form.status,
      productName: form.productName || null,
      exclusive: !!form.exclusive,
      isFlagged: !!form.isFlagged,
    }

    // Non-LIMS: unchanged — fire-and-forget, the parent owns the create + popup teardown.
    if (!showLims) {
      onSubmit(payload)
      return
    }

    // LIMS F-C1: after the sample is created, persist the (optional) QC details against the
    // new sample id. The parent's onSubmit returns the createSample dispatch promise (resolving
    // to the created sample), so we chain on it to obtain the id. If onSubmit returns nothing
    // (or the id can't be determined), we conservatively skip QC persistence and never break
    // the core create path. Only persist when at least one QC field was filled in.
    const result = onSubmit(payload)
    if (result && typeof result.then === "function" && qcHasContent(qc)) {
      result
        .then((res) => {
          const newSampleId = extractCreatedSampleId(res)
          if (newSampleId) {
            return dispatch(limsSampleQc.setForSample(newSampleId, qc))
          }
          return undefined
        })
        .catch(() => {})
    }
  }

  // Get analyses for the currently selected product
  const currentProductAnalyses = useMemo(() => {
    const productName = form.productName
    if (!productName || productName === '') return []

    const analysesForProduct = productAnalysis[productName]
    if (!analysesForProduct || !Array.isArray(analysesForProduct)) return []

    // Map to full analysis objects from analysisCollection and include the relationship ID
    const mapped = analysesForProduct
      .map((item) => {
        const analysisName = item.analysis_name
        const foundByName = analysisCollection.find((ac) => String(ac.name) === String(analysisName))
        if (foundByName) return { ...foundByName, relationshipId: item.id }
        const foundById = analysisCollection.find((ac) => String(ac.id) === String(analysisName))
        if (foundById) return { ...foundById, relationshipId: item.id }
        return null
      })
      .filter(Boolean)

    // Sort alphabetically by name
    return mapped.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [form.productName, productAnalysis, analysisCollection])

  const handleDeleteAnalysis = (relationshipId, analysisId) => {
    try {
      // Update prevProductRef to prevent auto-fill from re-triggering
      prevProductRef.current = form.productName

      dispatch(sampleView.removeProductAnalysis(relationshipId))

      // Also remove the analysis from the form's analysisName array if it's selected
      if (form.analysisName && form.analysisName.length > 0) {
        const updatedAnalysisName = form.analysisName.filter(id => id !== analysisId)
        setForm({ ...form, analysisName: updatedAnalysisName })
      }
    } catch (error) {
      console.error("Error removing product-analysis relationship:", error)
    }
  }

  return (
    <ConfigContainer title={title} submitHandler={submit} cancelHandler={onCancel}>
      <div className="add-sample-form">
        <div>
          <Container>
            <Title>{t("sampleName")}<div className="red-text">*</div></Title>
            <Text required value={form.sampleName} onChange={(v) => setForm({ ...form, sampleName: v })} />
          </Container>

          <Container>
            <Title>{t("productName")}<div className="red-text">*</div></Title>
            <Select
              collection={productCollectionFromAnalysis}
              value={form.productName || null}
              creatable={!isProductFromList.current}
              onChange={(items) => {
                const id = Array.isArray(items) ? items[0] : items
                // Track if the selected product is from the predefined list
                isProductFromList.current = id ? productCollectionFromAnalysis.some(p => p.id === id) : false
                // Clear analyses field when "None" is selected (id will be null or '')
                if (!id || id === '' || id === null) {
                  setForm({ ...form, productName: id, analysisName: [] })
                  // Reset prevProductRef so selecting the same product again will trigger auto-fill
                  prevProductRef.current = null
                } else {
                  // Just record the product. The actual Analyses pre-fill is done by the
                  // product-change effect — the legacy productAnalysis one when LIMS is off,
                  // or the curated-test-plan one (F-B2) when LIMS is on.
                  setForm({ ...form, productName: id })
                }
              }}
              isMulti={false}
              closeMenuOnSelect={true}
              includeNone={true}
            />
          </Container>

          <Container>
            <Title>{isEdit ? t("addAnalyses") : t("analyses")}{!isEdit && <div className="red-text">*</div>}</Title>
            <div className="AddEditSamplePopup-analyses-container">
              <div className="AddEditSamplePopup-analyses-select">
                <Select
                  collection={availableAnalysisCollection}
                  value={form.analysisName}
                  onChange={(items) => {
                    if (!items || items.length === 0) {
                      setForm({ ...form, analysisName: [] })
                    } else {
                      const ids = Array.isArray(items) ? items : [items]
                      const normalized = ids.map((it) => (it && it.id !== undefined ? it.id : it))
                      setForm({ ...form, analysisName: normalized })
                    }
                  }}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  includeNone={false}
                />
              </div>
              {!isEdit && (
                <Button
                  variant="primary"
                  onClick={() => setShowAnalysesPopup(true)}
                  disabled={!form.productName || form.productName === ''}
                  title={t("viewAllAnalysesForProduct")}
                >
                  <FiSettings />
                </Button>
              )}
            </div>
          </Container>

          <Container>
            <Title>{t("deadline")}<div className="red-text">*</div></Title>
            <div className="AddEditSamplePopup-datetime-container">
              <Text type="date" required value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} />
              <Text type="time" required value={form.deadlineTime} onChange={(v) => setForm({ ...form, deadlineTime: v })} />
            </div>
          </Container>

          {/* Show Priority field always when editing */}
          {isEdit && (
            <Container>
              <Title>{t("priority")}<div className="red-text">*</div></Title>
              <Select
                collection={[
                  { id: 1, name: `1 ${t("priorityHigh")}` },
                  { id: 2, name: `2 ${t("priorityMedium")}` },
                  { id: 3, name: `3 ${t("priorityLow")}` }
                ]}
                value={form.priority}
                onChange={(value) => setForm({ ...form, priority: value })}
                isMulti={false}
                closeMenuOnSelect={true}
                includeNone={false}
              />
            </Container>
          )}
          {/* When editing we only show minimal editable fields; otherwise show the extra fields under Show more */}
          {!isEdit && (
            <>
              <div className="AddEditSamplePopup-show-more-container">
                <div />
                <Button variant="link" onClick={() => setShowMore(!showMore)}>{showMore ? t("hideMore") : t("showMore")}</Button>
              </div>

              {showMore && (
                <>
                  <Container>
                    <Title>{t("arrival")}<div className="red-text">*</div></Title>
                    <div className="AddEditSamplePopup-datetime-container">
                      <Text type="date" required value={form.arrival} onChange={(v) => setForm({ ...form, arrival: v })} />
                      <Text type="time" required value={form.arrivalTime} onChange={(v) => setForm({ ...form, arrivalTime: v })} />
                    </div>
                  </Container>

                  <Container>
                    <Title>{t("priority")}<div className="red-text">*</div></Title>
                    <Select
                      collection={[
                        { id: 1, name: `1 ${t("priorityHigh")}` },
                        { id: 2, name: `2 ${t("priorityMedium")}` },
                        { id: 3, name: `3 ${t("priorityLow")}` }
                      ]}
                      value={form.priority}
                      onChange={(value) => setForm({ ...form, priority: value })}
                      isMulti={false}
                      closeMenuOnSelect={true}
                      includeNone={false}
                    />
                  </Container>

                  <Container>
                    <Title>{t("sampleId")}</Title>
                    {/* #4145 — the ONE field that is both a display and a payload. A sample that
                        handed its number to a rerun stores it with an invisible marker + uuid
                        appended, so it MUST show the bare number here; but `form.observationId` is
                        posted straight back on save (`observationId` in both the edit and the add
                        payload), and sending the bare number would try to claim a number the rerun
                        row now owns — a unique-constraint collision. So: show the clean value, and
                        re-attach the stored suffix to whatever the user types. On an ordinary
                        sample (no marker) both helpers are identities and this behaves exactly as
                        it always has. */}
                    <Text
                      value={displaySampleId(form.observationId)}
                      onChange={(v) => setForm({ ...form, observationId: restoreSampleIdSuffix(form.observationId, v) })}
                    />
                  </Container>

                  <Container>
                    <Title>{t("numberOfObservations")}</Title>
                    <Text value={form.numberOfObservations} onChange={(v) => setForm({ ...form, numberOfObservations: parseInt(v, 10) || 0 })} />
                  </Container>

                  <Container>
                    <Title>{t("exclusive")}</Title>
                    <Checkbox
                      id="sample-exclusive"
                      checked={!!form.exclusive}
                      onChange={(checked) => setForm({ ...form, exclusive: checked })}
                    />
                  </Container>
                </>
              )}
            </>
          )}

          {/* LIMS F-C1: optional QC sample-login details, shown only when LIMS is on. Mirrors
              the collapsible "Specification" section pattern from AnalysisConfigurationPage. All
              fields are optional; persisted separately via the limsSampleQc slice. */}
          {showLims && (
            <>
              <hr />
              <div className="AddEditSamplePopup-show-more-container">
                <h4 className="AddEditSamplePopup-qc-heading">{t("limsSampleQcDetails")}</h4>
                <Button variant="link" onClick={() => setShowQc(!showQc)}>
                  {showQc ? <FaArrowUp className="fa-arrowUp-icon" /> : <FiPlus className="fi-plus-icon" />}
                </Button>
              </div>

              {showQc && (
                <>
                  <Container>
                    <Title>{t("limsSampleQcUniqueId")}</Title>
                    <Text value={qc.uniqueSampleId} onChange={(v) => setQc({ ...qc, uniqueSampleId: v })} />
                  </Container>

                  <Container>
                    <Title>{t("limsSampleQcBatchLot")}</Title>
                    <Text
                      value={qc.batchLot}
                      onChange={(v) => setQc({ ...qc, batchLot: v })}
                      disabled={savedLotHeld}
                      title={savedLotHeld ? t("limsSampleQcBatchLotHeld") : undefined}
                    />
                    {savedLotHeld && <p className="AddEditSamplePopup-lot-held">{t("limsSampleQcBatchLotHeld")}</p>}
                  </Container>

                  <Container>
                    <Title>{t("limsSampleQcSampleType")}</Title>
                    <Text value={qc.sampleType} onChange={(v) => setQc({ ...qc, sampleType: v })} />
                  </Container>

                  <Container>
                    <Title>{t("limsSampleQcQuantity")}</Title>
                    <Number value={qc.quantity} onChange={(v) => setQc({ ...qc, quantity: v })} />
                  </Container>

                  <Container>
                    <Title>{t("limsSampleQcQuantityUnit")}</Title>
                    <Text value={qc.quantityUnit} onChange={(v) => setQc({ ...qc, quantityUnit: v })} />
                  </Container>

                  <Container>
                    <Title>{t("limsSampleQcSampledBy")}</Title>
                    <Select
                      collection={memberCollection}
                      value={qc.sampledBy || null}
                      onChange={(items) => {
                        const id = Array.isArray(items) ? items[0] : items
                        setQc({ ...qc, sampledBy: id || null })
                      }}
                      isMulti={false}
                      closeMenuOnSelect={true}
                      includeNone={true}
                    />
                  </Container>

                  <Container>
                    <Title>{t("limsSampleQcReceivedBy")}</Title>
                    <Select
                      collection={memberCollection}
                      value={qc.receivedBy || null}
                      onChange={(items) => {
                        const id = Array.isArray(items) ? items[0] : items
                        setQc({ ...qc, receivedBy: id || null })
                      }}
                      isMulti={false}
                      closeMenuOnSelect={true}
                      includeNone={true}
                    />
                  </Container>

                  <Container>
                    <Title>{t("limsSampleQcStorageCondition")}</Title>
                    <Text value={qc.storageCondition} onChange={(v) => setQc({ ...qc, storageCondition: v })} />
                  </Container>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Analyses Popup */}
      {showAnalysesPopup && (
        <Popup
          title={t("analysesRelatedTo", { product: form.productName || t("selectedProduct") })}
          onCancel={() => setShowAnalysesPopup(false)}
          className="AddEditSamplePopup-analyses-popup"
        >
          <div className="AddEditSamplePopup-analyses-list">
            {currentProductAnalyses.length > 0 ? (
              <div className="AddEditSamplePopup-analyses-items">
                {currentProductAnalyses.map((analysis, index) => (
                  <div key={analysis.id || index} className="AddEditSamplePopup-analysis-item">
                    <div className="AddEditSamplePopup-analysis-content">
                      <span className="AddEditSamplePopup-analysis-name">{analysis.name}</span>
                      {analysis.description && (
                        <span className="AddEditSamplePopup-analysis-description">{analysis.description}</span>
                      )}
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      className="AddEditSamplePopup-delete-button"
                      onClick={() => handleDeleteAnalysis(analysis.relationshipId, analysis.id)}
                      title={t("removeAnalysisFromProduct", { name: analysis.name })}
                    >
                      <FiTrash2 />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="AddEditSamplePopup-no-analyses">
                {t("noAnalysesForProduct")}
              </div>
            )}
          </div>
        </Popup>
      )}
    </ConfigContainer>
  )
}
